import { z } from 'zod';
import { blankToUndefined } from '@shared/error-message';
import { ErrorFieldSchema, errorFieldCode } from '@shared/schemas/error-envelope';
import {
  AdminMeResponseSchema,
  AdminRoleMutationResponseSchema,
  AdminRoleRosterResponseSchema,
  AdminRoleUserResponseSchema,
  ImpersonationPreviewResponseSchema,
  ImpersonationSortFactsResponseSchema,
  type AdminMeResponse,
  type AdminRoleMutationResponse,
  type AdminRoleRosterResponse,
  type AdminRoleUserResponse,
  type ImpersonationPreviewResponse,
  type ImpersonationSortFactsResponse,
} from '@shared/schemas/admin-access';
import type { ApiFetch } from '../lib/apiFetchJson';
import type { ImpersonationCommand } from '@shared/schemas/impersonation';
import { fetchJsonOnHttp } from '../lib/fetchJsonOnHttp';

// ─── API functions ───────────────────────────────────────────────────────────

/**
 * A failed admin API call, carrying what the server actually said.
 *
 * The admin routes answer in two shapes: `problem()` sets `title`
 * (`user_not_found`, `cannot_start_self`, `Forbidden`), while a rejected body
 * sets `error` (`validation_failed`). Callers that wrap a status in a string
 * lose both — which is why the same three friendly messages were re-derived at
 * each call site, and why an unrecognized body reached users verbatim.
 */
export class AdminApiError extends Error {
  /**
   * The server's `title` or `error`, whichever it sent, **lower-cased**;
   * `unknown_error` if neither. Casing is normalized because the admin routes
   * are not consistent about it — `requireImpersonateRole` answers
   * `title: 'Unauthorized'` while the route bodies answer `title: 'unauthorized'`
   * (`src/api/admin.ts`) — and a caller matching one spelling silently falls
   * through for the other.
   */
  readonly code: string;

  /**
   * The server's `detail`, for logs only. It is deliberately NOT the `message`:
   * every admin route's 500 handler sets `detail` to the caught exception text
   * (`detail: msg` in `src/api/admin.ts`), so a Prisma or connection error would
   * otherwise be rendered straight onto the page by the screens that display
   * `error.message` (`AdminRoles.tsx:140`, `AdminMapping.tsx:68`).
   */
  readonly detail?: string;

  constructor(code: string, readonly status: number, opts: { message?: string; detail?: string } = {}) {
    super(opts.message ?? `${code.toLowerCase()} (${status})`);
    this.name = 'AdminApiError';
    this.code = code.toLowerCase();
    this.detail = opts.detail;
  }
}

/** The three string fields an admin route's failure body may carry — `problem()`
 *  sets `title`/`detail`, a rejected body sets `error`. Each is read on its own
 *  so one wrongly-typed field does not hide the others. */
const AdminErrorBodySchema = z.object({
  title: z.string().optional().catch(undefined),
  error: ErrorFieldSchema.optional().catch(undefined),
  detail: z.string().optional().catch(undefined),
});

/**
 * Read the server's reason off a failed call (`ApiProblemError.status` / `.body`;
 * a non-JSON body such as a 502 HTML page leaves `body` undefined and cannot
 * mask the real failure).
 *
 * The user-facing `message` comes only from `byStatus`, the caller's own safe
 * copy; otherwise it is the code and status. The server's `detail` rides along
 * on the error for logs but never becomes the message, because a 500's detail
 * is the raw caught exception.
 */
export function adminApiErrorFromBody(
  status: number,
  body: unknown,
  byStatus: Record<number, string> = {},
): AdminApiError {
  const parsed = AdminErrorBodySchema.safeParse(body);
  const fields = parsed.success ? parsed.data : {};
  const code = blankToUndefined(fields.title) ?? (fields.error && errorFieldCode(fields.error)) ?? 'unknown_error';
  return new AdminApiError(code, status, { message: byStatus[status], detail: fields.detail });
}

/** `fetchJson`, with a non-2xx re-thrown as the `AdminApiError` this module's
 *  callers branch on. Network/abort/malformed failures and a policy refusal
 *  (`WriteRefusedError`) pass through as the `ApiProblemError` they already are. */
async function adminFetchJson<S extends z.ZodType>(
  apiFetch: ApiFetch,
  schema: S,
  input: string,
  init?: Parameters<ApiFetch>[1],
  byStatus: Record<number, string> = {},
): Promise<z.output<S>> {
  return fetchJsonOnHttp(apiFetch, schema, input, init, (err): never => {
    throw adminApiErrorFromBody(err.status, err.body, byStatus);
  });
}

/**
 * POST /api/admin/me/mapping. Only success or failure matters to callers, so
 * the 2xx body is accepted as-is: a body that does not match the response
 * schema must not read as a failed start/stop once the server has committed it.
 */
export async function submitImpersonationMapping(apiFetch: ApiFetch, command: ImpersonationCommand): Promise<void> {
  await adminFetchJson(apiFetch, z.unknown(), '/api/admin/me/mapping', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(command),
  });
}

export async function fetchAdminMe(apiFetch: ApiFetch): Promise<AdminMeResponse> {
  return adminFetchJson(apiFetch, AdminMeResponseSchema, '/api/admin/me');
}

/** What the ⌘I palette shows about one person before switching to them. */
export async function fetchImpersonationPreview(apiFetch: ApiFetch, slackUserId: string): Promise<ImpersonationPreviewResponse> {
  return adminFetchJson(
    apiFetch,
    ImpersonationPreviewResponseSchema,
    `/api/admin/me/preview?slackUserId=${encodeURIComponent(slackUserId)}`,
  );
}

/** Everyone's sort values for the ⌘I list (next meetings, last meeting, last active). */
export async function fetchImpersonationSortFacts(apiFetch: ApiFetch): Promise<ImpersonationSortFactsResponse> {
  return adminFetchJson(apiFetch, ImpersonationSortFactsResponseSchema, '/api/admin/me/sort-facts');
}

/**
 * Grant or revoke a role — the one writer for `POST`/`DELETE /api/admin/roles`.
 *
 * Both admin screens built this request and hand-parsed its failure
 * separately, which is how their copy drifted (one said "You cannot remove the
 * final admin", the other "Cannot remove the final admin") and how
 * `AdminRoles` came to render the server's `detail` — the raw exception on a
 * 500. Callers shape their own success handling; the refusal is
 * `describeRoleError`.
 */
export async function setAdminRole(
  apiFetch: ApiFetch,
  payload: { email: string; roleSlug: string; grant: boolean },
): Promise<AdminRoleMutationResponse> {
  return adminFetchJson(apiFetch, AdminRoleMutationResponseSchema, '/api/admin/roles', {
    method: payload.grant ? 'POST' : 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email: payload.email, roleSlug: payload.roleSlug }),
  });
}

/**
 * What a role operation refused, in words. One copy for both admin screens.
 *
 * `op` is required rather than defaulted: reading the roster and granting a
 * role fail through the same codes but are not the same sentence, and a
 * default silently told a failed *read* "Unable to update roles".
 */
export function describeRoleError(error: unknown, op: 'load' | 'update'): string {
  const fallback = op === 'load' ? 'Unable to load roles.' : 'Unable to update roles.';
  if (!(error instanceof AdminApiError)) {
    return error instanceof Error && error.message ? error.message : fallback;
  }
  switch (error.code) {
    case 'cannot_revoke_last_admin': return 'You cannot remove the final admin. Assign another admin first.';
    case 'user_not_found': return 'User not found for that email.';
    case 'forbidden':
      return op === 'load'
        ? 'You do not have permission to view roles.'
        : 'You do not have permission to manage roles.';
    case 'unauthorized': return 'Your session expired — sign in again.';
    default: return `${fallback.replace(/\.$/, '')} (${error.status}).`;
  }
}

/**
 * What the impersonation mapping endpoint refused, in words. Endpoint
 * vocabulary lives with the endpoint, beside `AdminApiError` and
 * `describeRoleError`, so a surface reads copy from one place — otherwise a
 * machine string like `cannot_start_self` reaches users on whichever surface
 * forgets.
 */
export function describeImpersonationError(error: unknown): string {
  if (!(error instanceof AdminApiError)) {
    return error instanceof Error && error.message ? error.message : 'Switch failed.';
  }
  // `code` is lower-cased, so the routes' `Unauthorized` / `unauthorized` split
  // cannot make one of them fall through to the default.
  switch (error.code) {
    case 'user_not_found': return 'That person has no Terra user.';
    case 'cannot_start_self': return 'You are already acting as yourself.';
    case 'forbidden': return 'You do not have permission to switch users.';
    case 'unauthorized': return 'Your session expired — sign in again.';
    case 'role_not_seeded': return 'Impersonation is not configured on this environment.';
    case 'validation_failed': return 'Terra sent a request this server did not understand.';
    case 'demo_disabled': return 'Demo mode is turned off right now.';
    case 'demo_identity_unusable': return 'That demo identity is not ready. Ask an admin to check it.';
    case 'target_forbidden': return 'You do not have permission to start that session.';
    default: return `Switch failed (${error.status}).`;
  }
}

export async function fetchAdminRoleRoster(apiFetch: ApiFetch): Promise<AdminRoleRosterResponse> {
  return adminFetchJson(apiFetch, AdminRoleRosterResponseSchema, '/api/admin/roles', undefined, {
    403: 'You do not have permission to view roles.',
  });
}

export async function fetchAdminUserRoles(apiFetch: ApiFetch, email: string): Promise<AdminRoleUserResponse> {
  const normalized = email.trim().toLowerCase();
  if (!normalized) {
    throw new Error('Owner email is required');
  }
  return adminFetchJson(
    apiFetch,
    AdminRoleUserResponseSchema,
    `/api/admin/users/roles?${new URLSearchParams({ email: normalized })}`,
    undefined,
    {
      404: 'No user found for that email.',
      400: 'A valid email is required.',
    },
  );
}
